import React, { useState } from "react";
import type { SlotWeek } from "./slot-types";
import type { ContactRequestContentProps } from "../../content/component-contact-request";

export type SlotPickerProps = {
    content: ContactRequestContentProps;
    weeks: SlotWeek[];
    language: "de" | "en";
    timeZone: string;
    /** ISO-Startzeit des gewaehlten Slots, oder `null` wenn keiner gewaehlt ist. */
    selected: string | null;
    onSelect: (slot: { start: string; end: string } | null) => void;
};

/**
 * Wochenweise Slot-Auswahl (Endgame v0.3, Etappe F2b).
 *
 * Warum geblaettert und nicht alles auf einmal: Jans Kalender ergibt ueber
 * zwei Wochen rund 100 freie Slots. Als flache Liste waere das eine Wand, durch
 * die niemand scrollt — gemessen am 2.8.2026. Die Gruppierung passiert im
 * Server (`groupByWeek`), hier wird nur geblaettert; es geht dabei kein Slot
 * verloren.
 */
export const SlotPicker: React.FC<SlotPickerProps> = ({ content, weeks, language, timeZone, selected, onSelect }) => {
    const [weekIndex, setWeekIndex] = useState(0);
    const week = weeks[weekIndex];

    // Die Zeitzone kommt vom Server mit, statt der Browser-Zone zu vertrauen:
    // ein Besucher in Berlin soll dieselbe Uhrzeit lesen wie einer in Zuerich,
    // sonst bucht er 09:00 und erscheint um 08:00.
    const locale = language === "en" ? "en-GB" : "de-CH";
    const timeFormat = new Intl.DateTimeFormat(locale, {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
    const dayFormat = new Intl.DateTimeFormat(locale, {
        timeZone,
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
    });

    if (!week) {
        return null;
    }

    return (
        <div className="rounded-md border border-gray-200 p-3">
            <div className="mb-3 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setWeekIndex(i => Math.max(0, i - 1))}
                    disabled={weekIndex === 0}
                    className="rounded px-3 py-1 text-sm font-medium text-primary disabled:opacity-40"
                    aria-label={content.previousWeek}
                >
                    ←
                </button>
                <span className="text-sm font-medium text-gray-700">
                    {content.weekOf} {dayFormat.format(new Date(`${week.weekStart}T12:00:00Z`))}
                </span>
                <button
                    type="button"
                    onClick={() => setWeekIndex(i => Math.min(weeks.length - 1, i + 1))}
                    disabled={weekIndex >= weeks.length - 1}
                    className="rounded px-3 py-1 text-sm font-medium text-primary disabled:opacity-40"
                    aria-label={content.nextWeek}
                >
                    →
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                {week.days.map(day => (
                    <div key={day.date}>
                        <div className="mb-1 text-xs font-semibold uppercase text-gray-500">
                            {dayFormat.format(new Date(`${day.date}T12:00:00Z`))}
                        </div>
                        <div className="flex flex-col gap-1">
                            {day.slots.map(slot => {
                                const isSelected = selected === slot.start;
                                return (
                                    <button
                                        key={slot.start}
                                        type="button"
                                        // Erneutes Klicken hebt die Wahl auf — sonst
                                        // kaeme der Besucher nie wieder zu einer
                                        // reinen Nachricht zurueck.
                                        onClick={() => onSelect(isSelected ? null : slot)}
                                        aria-pressed={isSelected}
                                        className={
                                            "rounded border px-2 py-1 text-sm transition-colors " +
                                            (isSelected
                                                ? "border-primary bg-primary text-white"
                                                : "border-gray-300 text-gray-700 hover:border-primary")
                                        }
                                    >
                                        {timeFormat.format(new Date(slot.start))}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
